import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'

type Question = {
  question: string
  why: string
  good: string
  fairmeadow: string
  proofLabel: string
  proofHref: string
}

const questions: Question[] = [
  {
    question: 'What does your reporting cadence actually look like?',
    why: 'Vague reporting promises ("we keep investors informed") are the most common red flag. Sophisticated LPs want to see the actual artifact before they wire capital.',
    good: 'A specific cadence (e.g. quarterly), specific contents (property-level P&L, rent roll, occupancy, market conditions), and specific delivery (PDF, dashboard, email).',
    fairmeadow:
      'Quarterly property-level reports covering rent roll, occupancy, operating P&L, capex, and market notes. Investors see the full artifact for Deal #1 on the Track Record page before they ever commit capital.',
    proofLabel: 'See the Track Record',
    proofHref: '/track-record',
  },
  {
    question: 'How do you stress-test a deal before closing?',
    why: '"Conservative underwriting" is claimed by every operator in the space. The only meaningful version of the answer names specific adverse scenarios the model survives.',
    good: 'Explicit scenarios: rising vacancy, rate hikes at refinance, deferred maintenance, rent stagnation. The pro forma must still produce positive cash flow under each.',
    fairmeadow:
      'Every deal is modeled against rising vacancies, rate hikes, and deferred maintenance before we offer. If the numbers break under pressure, we pass — full stop. The framework lives on the Resources page.',
    proofLabel: 'Read the Underwriting Framework',
    proofHref: '/resources/underwriting-basics',
  },
  {
    question: "What's your track record — and is it public?",
    why: 'Paper underwriting is different from a real closed deal. Many operators talk in pipeline. Few publish actual closed-deal financials.',
    good: 'A complete list of closed deals with purchase price, financing structure, current rent, occupancy, and operating performance against the original underwriting.',
    fairmeadow:
      "One deal closed (West Boulevard duplex, April 2025). Full purchase, financing, rent, and occupancy on the public Track Record page — not gated behind an investor portal. We'd rather be honest about being early than pad the deck.",
    proofLabel: 'View Deal #1',
    proofHref: '/track-record',
  },
  {
    question: 'Where is your own capital at risk in each deal?',
    why: "Operator alignment is structural, not philosophical. If they don't have meaningful capital next to yours, their incentive is volume — not outcomes.",
    good: 'Founder/GP equity contribution in every deal, disclosed at the deal level. Not just acquisition fees and a promote.',
    fairmeadow:
      'Founder equity in every deal. Garrett invests his own capital alongside LPs in each acquisition — not as a sweep into a fund-level vehicle. The percentages and absolute dollars are disclosed in each deal memo.',
    proofLabel: "Read Garrett's Story",
    proofHref: '/about',
  },
  {
    question: 'What happens if a deal underperforms the underwriting?',
    why: 'Most operators only rehearse the success pitch. The adverse-scenario answer reveals whether they have a real plan or just hope.',
    good: 'A written plan: trigger metrics, communication cadence under stress, capital-call vs. distribution-pause vs. refinance options, and how decisions get made.',
    fairmeadow:
      'A written adverse-scenario plan attached to every deal memo: trigger thresholds (occupancy, DSCR), monthly communication (instead of quarterly) once a threshold is crossed, and a clear hierarchy — preserve capital, then preserve cash flow, then optimize for return.',
    proofLabel: 'See the Strategy',
    proofHref: '/strategy',
  },
]

export default function OperatorQuestionsPage() {
  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="border-border border-b">
        <div className="mx-auto max-w-5xl px-5 pt-14 pb-12 md:px-8 md:pt-20 md:pb-16">
          <h1 className="text-fluid-h1 text-navy mb-6 max-w-4xl">Five questions to ask any real estate operator</h1>
          <p className="text-fluid-body-lg text-muted-foreground max-w-2xl">
            Use these to vet anyone you're considering investing with — including us. We've answered each one with what
            you should be looking for, and what we actually do.
          </p>
        </div>
      </section>

      {/* Questions Section */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-4xl space-y-8">
          {questions.map((q, idx) => {
            return (
              <GlassCard key={q.question} accent className="question-card">
                <div className="mb-6 flex items-start gap-4">
                  <div>
                    <p className="text-muted-foreground mb-2 text-sm">Question {idx + 1}</p>
                    <h2 className="text-foreground text-2xl font-bold md:text-3xl">{q.question}</h2>
                  </div>
                </div>

                <div className="space-y-5 pl-0 md:pl-16">
                  <div>
                    <p className="text-navy mb-2 text-sm font-semibold">Why it matters</p>
                    <p className="text-muted-foreground">{q.why}</p>
                  </div>

                  <div>
                    <p className="text-navy mb-2 text-sm font-semibold">What a good answer looks like</p>
                    <p className="text-muted-foreground">{q.good}</p>
                  </div>

                  <div className="border-primary border-l-2 py-1 pl-5">
                    <p className="text-navy mb-2 text-sm font-semibold">How Fairmeadow answers</p>
                    <p className="text-foreground mb-4">{q.fairmeadow}</p>
                    <Link
                      to={q.proofHref}
                      className="text-primary hover:text-primary/80 group inline-flex items-center font-medium transition-colors"
                    >
                      {q.proofLabel}
                    </Link>
                  </div>
                </div>
              </GlassCard>
            )
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <GlassCard accent className="reveal text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Want to ask us something else?</h2>
            <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
              Take the fit assessment, or send a direct message. Either way you'll hear from Garrett within 1-2 business
              days.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/fit">
                <Button size="xl">Take the Fit Assessment</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="xl">
                  Send a Message
                </Button>
              </Link>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
